/*
 * gldn-consent.js — consent gating helper for GLDN  (gldn54 / issue #289)
 * ---------------------------------------------------------------------------
 * Bridges GLDN's hardcoded, non-GTM, non-Shopify-framework trackers to the
 * visitor's consent decision. Reads Shopify's Customer Privacy API, which the
 * Consentmo banner populates (works on every Consentmo plan, including free).
 *
 * REGION-AGNOSTIC. It reads the *resolved* consent decision, so it honors EU
 * opt-in (blocked until accept) and US opt-out (allowed until opt-out) with no
 * region branching here — Consentmo + Shopify decide what's allowed per region.
 *
 * SAFETY
 *   - Enforcement is OFF by default: every gate is pure pass-through until
 *     window.gldnConsentConfig.enforce === true. Nothing changes behaviorally
 *     until that flag is flipped at cutover (Consentmo embed live + QA'd).
 *   - Even when enforcing it FAILS OPEN: if the Customer Privacy API is
 *     unavailable or throws, trackers fire exactly as they do today. This can
 *     never silently kill analytics.
 *
 * USAGE (at each tracker's load point)
 *   if (window.gldnConsent) gldnConsent.whenMarketingAllowed(loadPixel);
 *   else loadPixel();                       // fail-open if helper missing
 *
 * GTM TOGGLE
 *   gldnConsent.gateGtm is true only when enforcing AND config.gateGtm !== false.
 *   Set config.gateGtm = false once Consentmo Plus/Standard Consent Mode v2 is
 *   live (it gates the GTM-delivered tags itself; we stop double-gating GTM).
 */
(function (w, d) {
  'use strict';

  var cfg = w.gldnConsentConfig || {};
  var ENFORCE = cfg.enforce === true;       // default false → pass-through
  var DEBUG = cfg.debug === true;
  var GATE_GTM = cfg.gateGtm !== false;     // default true

  function log() {
    if (DEBUG && w.console && w.console.log) {
      w.console.log.apply(w.console, ['[gldn-consent]'].concat([].slice.call(arguments)));
    }
  }

  // Our purpose names → Customer Privacy API predicate methods.
  var PREDICATES = {
    marketing: 'marketingAllowed',
    analytics: 'analyticsProcessingAllowed',
    preferences: 'preferencesProcessingAllowed',
    saleOfData: 'saleOfDataAllowed'
  };

  function cp() {
    return (w.Shopify && w.Shopify.customerPrivacy) || null;
  }

  // Is `purpose` currently allowed? Returns true (fail-open) whenever the API
  // or predicate is missing / throws.
  function isAllowed(purpose) {
    var api = cp();
    var method = PREDICATES[purpose];
    if (!api || typeof api[method] !== 'function') return true; // fail-open
    try {
      return api[method]() === true;
    } catch (e) {
      log('predicate threw, failing open:', purpose, e);
      return true;
    }
  }

  // Load the Customer Privacy API, then resolve readiness. cb(available).
  var readyCbs = [];
  var apiState = 'pending'; // 'pending' | 'ready' | 'unavailable'

  function flushApi(available) {
    apiState = available ? 'ready' : 'unavailable';
    var list = readyCbs;
    readyCbs = [];
    for (var i = 0; i < list.length; i++) {
      try { list[i](available); } catch (e) { /* isolate */ }
    }
  }

  function whenApiReady(cb) {
    if (apiState === 'ready') return cb(true);
    if (apiState === 'unavailable') return cb(false);
    readyCbs.push(cb);
  }

  var apiInitStarted = false;
  function initApi() {
    if (apiInitStarted) return;
    apiInitStarted = true;
    if (cp()) { flushApi(true); return; }
    // Ask Shopify to load the API if its loader is already present (speeds
    // things up); harmless if it isn't.
    if (w.Shopify && typeof w.Shopify.loadFeatures === 'function') {
      try {
        w.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () {});
      } catch (e) { /* fall through to polling */ }
    }
    // CRITICAL: window.Shopify / customerPrivacy are populated ASYNCHRONOUSLY
    // by Shopify + the CMP (Consentmo), typically after this head script runs.
    // Poll until the API appears instead of giving up immediately — giving up
    // early would fail-open every gate before consent is even known. Fail open
    // only after a real timeout, so a page with NO consent API still tracks as
    // it does today.
    var waited = 0, STEP = 100, MAX = 8000;
    var timer = w.setInterval(function () {
      if (cp()) { w.clearInterval(timer); flushApi(true); return; }
      waited += STEP;
      if (waited >= MAX) { w.clearInterval(timer); flushApi(false); }
    }, STEP);
  }

  // Core: run `fn` the first moment `purpose` is allowed.
  function when(purpose, fn) {
    if (typeof fn !== 'function') return;

    // Enforcement off → behave exactly as today.
    if (!ENFORCE) { fn(); return; }

    whenApiReady(function (available) {
      if (!available) { log('API unavailable, fail-open:', purpose); fn(); return; }

      var fired = false;
      function tryFire() {
        if (fired) return;
        if (isAllowed(purpose)) {
          fired = true;
          d.removeEventListener('visitorConsentCollected', tryFire);
          log('allowed → firing:', purpose);
          fn();
        }
      }
      // Allowed now? (opt-out regions, or the visitor already consented.)
      tryFire();
      // Opt-in region / not yet decided → fire when the choice arrives.
      if (!fired) {
        d.addEventListener('visitorConsentCollected', tryFire);
        log('waiting for consent:', purpose);
      }
    });
  }

  w.gldnConsent = w.gldnConsent || {
    when: when,
    whenMarketingAllowed: function (fn) { when('marketing', fn); },
    whenAnalyticsAllowed: function (fn) { when('analytics', fn); },
    whenSaleOfDataAllowed: function (fn) { when('saleOfData', fn); },
    isAllowed: isAllowed,
    enforced: ENFORCE,
    gateGtm: ENFORCE && GATE_GTM,
    version: '1'
  };

  if (ENFORCE) initApi();
})(window, document);
